/**
 * planMath — Paket G (premium-checkout-redesign)
 *
 * Reine Rechenfunktionen für Upgrade/Downgrade-Vorschau, Restwert und
 * Laufzeit-Umrechnung. Exakt die BE-Konvention: Tagessatz = Monatspreis / 30
 * (NICHT x12/365 wie im Design-Prototyp-Kommentar abweichend vermerkt).
 */
import type { Plan } from '../subscription/useSubscriptionPlans'
import { activeBcp47 } from '../../lib/locale'
import { formatSubscriptionDate } from '../subscription/subscriptionUtils'

/** Nur für Badge-Texte (Rabatt-Prozent je Laufzeit) — Preise kommen IMMER aus durations[].price. */
export const TERM_DISCOUNT_PCT: Record<1 | 3 | 6 | 12, number> = { 1: 0, 3: 10, 6: 20, 12: 30 }

/**
 * Rang eines Plans im Katalog, sortiert nach price_eur_monthly aufsteigend.
 * Deckt sowohl den Normalfall (BE liefert bereits tier-sortiert = preis-
 * aufsteigend) als auch den Fallback (unsortierte Eingabe) mit derselben
 * Sortierung ab. -1, wenn der Plan nicht im Katalog vorkommt.
 */
export function planRank(plans: Pick<Plan, 'id' | 'price_eur_monthly'>[], planId: string): number {
  const sorted = [...plans].sort((a, b) => a.price_eur_monthly - b.price_eur_monthly)
  return sorted.findIndex((p) => p.id === planId)
}

export function isUpgrade(plans: Pick<Plan, 'id' | 'price_eur_monthly'>[], fromId: string, toId: string): boolean {
  return planRank(plans, toId) > planRank(plans, fromId)
}

export function isDowngrade(plans: Pick<Plan, 'id' | 'price_eur_monthly'>[], fromId: string, toId: string): boolean {
  return planRank(plans, toId) < planRank(plans, fromId)
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

/** Tagessatz gemäß BE-Konvention: Monatspreis / 30. */
export function dailyRate(monthly: number): number {
  return monthly / 30
}

/** Restwert = Tagessatz * verbleibende Tage, auf 2 Nachkommastellen gerundet. */
export function remainingValue(monthly: number, daysRemaining: number): number {
  return round2(dailyRate(monthly) * daysRemaining)
}

/** Ein Guthabenbetrag umgerechnet in Tage Laufzeit des Ziel-Plans. */
export function convertCreditToDays(credit: number, targetMonthly: number): number {
  if (targetMonthly <= 0) return Infinity
  return credit / dailyRate(targetMonthly)
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000)
}

/** Heute fällig: Laufzeitpreis abzüglich Guthaben, nie negativ. */
export function dueToday(termTotal: number, credit: number): number {
  return Math.max(0, termTotal - credit)
}

/** Guthaben-Überschuss über den Laufzeitpreis hinaus, umgerechnet in Zusatztage des Ziel-Plans. */
export function overflowDays(termTotal: number, credit: number, targetMonthly: number): number {
  const overflow = credit - termTotal
  if (overflow <= 0) return 0
  return convertCreditToDays(overflow, targetMonthly)
}

/** Tage in {Monate, Resttage} aufgeteilt (30.44-Tage-Monatsbasis wie im Design-Prototyp). */
export function splitMonthsDays(days: number): { months: number; days: number } {
  const months = Math.floor(days / 30.44)
  const rest = Math.round(days - months * 30.44)
  return { months, days: rest }
}

export function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat(activeBcp47(), {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/** Langes Datumsformat lokalisiert (delegiert an formatSubscriptionDate). */
export function formatDateLong(date: Date): string {
  return formatSubscriptionDate(date.toISOString())
}
